import type { PropsWithChildren } from 'react'
import type { TwStyle } from 'twin.macro'
import tw, { styled } from 'twin.macro'
import { ColorList, getPresetByName } from '@getcircuit/colors/routes'
import type { ColorPreset } from '@getcircuit/colors/routes'
import { deterministicSelection } from '@getcircuit/web-utils'

import { Person } from '../Icons'
import type { AvatarProps, AvatarSize } from './types'
import { useSafeState } from '../../hooks'

const spacingStyles: Record<AvatarSize, TwStyle> = {
  6: tw`w-6 h-6`,
  8: tw`w-8 h-8`,
  10: tw`w-12 h-12`,
  14: tw`w-14 h-14`,
  20: tw`w-20 h-20`,
}

const textStyles: Record<AvatarSize, TwStyle> = {
  6: tw`text-xs`,
  8: tw`text-base`,
  10: tw`text-xl`,
  14: tw`text-2xl`,
  20: tw`text-3xl`,
}

const defaultColor = {
  inactive: '#e2e8f0',
  text: '#64748b',
} as ColorPreset

function getAvatarColor({
  name,
  preset,
  colorless,
}: {
  name?: string | null
  preset?: AvatarProps['color']
  colorless?: boolean
} = {}) {
  if (colorless) {
    preset = defaultColor
  } else if (typeof preset === 'string') {
    preset = getPresetByName(preset)
  } else if (preset == null) {
    if (name) {
      preset = getPresetByName(deterministicSelection(name, ColorList))
    }
  }

  // guarantee default preset if needed
  preset ??= defaultColor

  return `
    background-color: ${preset.inactive};
    color: ${preset.text};
  `
}

const AvatarWrapper = styled.div(
  ({
    size = 14,
    name = '',
    $color,
    $colorless = false,
  }: {
    size: AvatarProps['size']
    name?: string
    $color: AvatarProps['color']
    $colorless?: boolean
  }) => [
    tw`
      max-w-full
      max-h-full
      overflow-hidden
      inline-flex justify-center items-center
      align-middle
      select-none
      rounded-full
      font-brand
      font-bold
      uppercase
  `,
    spacingStyles[size],
    textStyles[size],
    getAvatarColor({ name, preset: $color, colorless: $colorless }),
  ],
)

function getInitials(name: string) {
  const names = name.split(' ')

  // eslint-disable-next-line prefer-destructuring
  const firstName = names[0]
  const lastName = names.length > 1 ? names[names.length - 1] : ''

  return `${firstName[0]}${lastName ? lastName[0] : ''}`
}

const HAS_SPECIAL_CHAR_REGEX = /[^A-z]/

function Avatar({
  src,
  name,
  size,
  children,
  color,
  colorless,
  ...rest
}: PropsWithChildren<AvatarProps>) {
  const [error, setError] = useSafeState(false)

  if (src && !error) {
    return (
      <AvatarWrapper
        size={size}
        $color={color}
        $colorless={colorless}
        {...rest}
      >
        <img
          tw="w-full h-auto block text-xs"
          loading="lazy"
          src={src}
          alt={name ?? undefined}
          onError={() => setError(true)}
        />
      </AvatarWrapper>
    )
  }

  const initials = name ? getInitials(name) : undefined

  if (name && initials && !HAS_SPECIAL_CHAR_REGEX.test(initials)) {
    return (
      <AvatarWrapper
        size={size}
        name={name}
        title={name}
        $color={color}
        $colorless={colorless}
        {...rest}
      >
        {initials}
      </AvatarWrapper>
    )
  }

  return (
    <AvatarWrapper
      name={name ?? undefined}
      title={name ?? undefined}
      size={size}
      $color={color}
      $colorless={colorless}
      {...rest}
    >
      <Person tw="w-full h-1/2" title="Avatar Placeholder" />
    </AvatarWrapper>
  )
}

export default Avatar
